/**
 * Dynamically determine API URL based on current domain
 * 
 * This allows the display screens to work on alternative domains 
 * (e.g., infovegg.displaysystem.no for legacy screens with wildcard SSL)
 * while keeping app.infovegg.no as the main domain.
 */

// List of alternative domains that should use their own domain for API calls
const ALTERNATIVE_DOMAINS = [
  'infovegg.displaysystem.no'
];

/**
 * Get the API base URL
 * - For alternative domains: uses the current domain
 * - For main domain: uses the configured REACT_APP_BACKEND_URL
 * 
 * @returns {string} The API base URL (without /api suffix)
 */
export const getBackendUrl = () => {
  const currentHost = window.location.hostname;
  const currentProtocol = window.location.protocol;
  
  if (ALTERNATIVE_DOMAINS.includes(currentHost)) {
    // Use the current domain for API calls (alternative domain)
    return `${currentProtocol}//${currentHost}`;
  }
  
  // Default: use the configured backend URL
  return process.env.REACT_APP_BACKEND_URL;
};

/**
 * Get the full API URL (with /api suffix)
 * 
 * @returns {string} The full API URL
 */
export const getApiUrl = () => {
  return `${getBackendUrl()}/api`;
};

/**
 * Check if we're running on an alternative domain
 * 
 * @returns {boolean} True if on alternative domain
 */
export const isAlternativeDomain = () => {
  return ALTERNATIVE_DOMAINS.includes(window.location.hostname);
};

/**
 * Get the current hostname
 * 
 * @returns {string} The current hostname
 */
export const getCurrentHost = () => {
  return window.location.hostname;
};

/**
 * Get headers to include with API requests
 * Includes X-Request-Domain for secondary domain detection on backend
 * 
 * @returns {object} Headers object
 */
export const getApiHeaders = () => {
  const headers = {};
  
  if (isAlternativeDomain()) {
    headers['X-Request-Domain'] = getCurrentHost();
  }
  
  return headers;
};

export default getApiUrl;
